/**
 * InlineEditableSelect - View-first inline editable select dropdown
 *
 * Displays as plain text with a chevron dropdown affordance,
 * opens dropdown on click.
 *
 * Features:
 * - isChanged: Shows visual indicator when field has been modified
 * - compact: Reduces whitespace for dense layouts (same font size)
 * - isHighlighted: Highlights field (e.g., when hovering undo button)
 * - linkHref: When provided, the value text becomes a clickable link,
 *   and only the chevron icon opens the dropdown for editing
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ChevronsUpDown, Check } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { RequiredMarker } from '../../../../components/ui/label';

interface SelectOption {
  value: string;
  label: string;
  /** Optional color for the option (hex code or CSS color) */
  color?: string;
  /** Optional Lucide icon component */
  icon?: LucideIcon;
}

interface InlineEditableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  label?: string;
  /** Defaults to the translated `common:app.select`. */
  placeholder?: string;
  required?: boolean;
  className?: string;
  id?: string;
  /** Shows a visual indicator that this field has been modified */
  isChanged?: boolean;
  /** Reduces spacing for dense layouts */
  compact?: boolean;
  /** Highlights the field (e.g., when hovering undo for this field) */
  isHighlighted?: boolean;
  /** Disable editing (view only) */
  readOnly?: boolean;
  /** When provided, the value text becomes a link; only the pencil icon opens the dropdown */
  linkHref?: string;
  /** When true, shows a "None" option at the top to clear the value */
  clearable?: boolean;
  /** Label for the clear option. Defaults to the translated `common:app.none`. */
  clearLabel?: string;
}

export function InlineEditableSelect({
  value,
  onChange,
  options,
  label,
  placeholder,
  required,
  className,
  id,
  isChanged = false,
  compact = false,
  isHighlighted = false,
  readOnly = false,
  linkHref,
  clearable = false,
  clearLabel,
}: InlineEditableSelectProps) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const resolvedPlaceholder = placeholder ?? t('app.select');
  const resolvedClearLabel = clearLabel ?? t('app.none');

  const selectedOption = options.find((opt) => opt.value === value);
  const SelectedIcon = selectedOption?.icon;
  const displayValue = selectedOption?.label || resolvedPlaceholder;
  const isEmpty = !value;

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!readOnly) {
      setIsOpen(open);
    }
  };

  // When linkHref is provided, render a different layout where:
  // - The text is a clickable link
  // - The pencil icon triggers the dropdown
  if (linkHref && !readOnly) {
    return (
      <div className={cn(compact ? 'space-y-1' : 'space-y-2', className)}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
          >
            {label}
            {required && <RequiredMarker />}
            {isChanged && (
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-warning"
                title={t('app.modified')}
              />
            )}
          </label>
        )}
        <div
          className="relative group"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            id={id}
            className={cn(
              'flex w-full items-center rounded-md px-3 text-sm',
              'transition-colors duration-150',
              compact ? 'h-8 py-1' : 'h-10 py-2',
              isHovered && !isOpen && 'bg-muted/50',
              isOpen && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
              isHighlighted && 'bg-warning/10',
              isEmpty && 'text-placeholder'
            )}
          >
            {SelectedIcon && <SelectedIcon className="h-4 w-4 mr-2 flex-shrink-0 text-muted-foreground" />}
            {selectedOption?.color && (
              <span
                className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0"
                style={{ backgroundColor: selectedOption.color }}
              />
            )}
            <Link
              to={linkHref}
              className="flex-1 truncate text-primary hover:underline"
            >
              {displayValue}
            </Link>
            <Popover open={isOpen} onOpenChange={handleOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="ms-2 p-1 rounded hover:bg-muted transition-colors flex-shrink-0"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(true);
                  }}
                >
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[200px] p-1 overflow-y-auto"
                align="start"
                style={{ maxHeight: 'var(--radix-popover-content-available-height, 400px)' }}
                onWheel={(e) => {
                  const el = e.currentTarget;
                  const { scrollTop, scrollHeight, clientHeight } = el;
                  const atTop = scrollTop <= 0;
                  const atBottom = scrollTop + clientHeight >= scrollHeight;
                  if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) return;
                  el.scrollBy(0, e.deltaY);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <div className="flex flex-col">
                  {clearable && (
                    <button
                      onClick={() => handleSelect('')}
                      className={cn(
                        'flex items-center justify-between px-3 py-2 text-sm rounded-md',
                        'hover:bg-muted transition-colors text-muted-foreground',
                        isEmpty && 'bg-muted'
                      )}
                    >
                      <span>{resolvedClearLabel}</span>
                      {isEmpty && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  )}
                  {options.map((option) => {
                    const OptionIcon = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          'flex items-center justify-between px-3 py-2 text-sm rounded-md',
                          'hover:bg-muted transition-colors',
                          option.value === value && 'bg-muted'
                        )}
                      >
                        <span className="flex items-center gap-2">
                          {OptionIcon && <OptionIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                          {option.color && (
                            <span
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: option.color }}
                            />
                          )}
                          {option.label}
                        </span>
                        {option.value === value && (
                          <Check className="h-4 w-4 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(compact ? 'space-y-1' : 'space-y-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1.5"
        >
          {label}
          {required && <RequiredMarker />}
          {isChanged && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-warning"
              title={t('app.modified')}
            />
          )}
        </label>
      )}
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <div
            className="relative group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              id={id}
              role="combobox"
              aria-expanded={isOpen}
              className={cn(
                'flex w-full items-center rounded-md px-3 text-sm',
                'transition-colors duration-150',
                compact ? 'h-8 py-1' : 'h-10 py-2',
                !readOnly && 'cursor-pointer',
                !readOnly && isHovered && !isOpen && 'bg-muted/50',
                isOpen && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
                isHighlighted && 'bg-warning/10',
                isEmpty && 'text-placeholder'
              )}
            >
              {SelectedIcon && <SelectedIcon className="h-4 w-4 mr-2 flex-shrink-0 text-muted-foreground" />}
              {selectedOption?.color && (
                <span
                  className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0"
                  style={{ backgroundColor: selectedOption.color }}
                />
              )}
              <span className="flex-1 truncate">{displayValue}</span>
              {!readOnly && (
                <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
              )}
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[200px] p-1 overflow-y-auto"
          align="start"
          style={{ maxHeight: 'var(--radix-popover-content-available-height, 400px)' }}
          onWheel={(e) => {
            const el = e.currentTarget;
            const { scrollTop, scrollHeight, clientHeight } = el;
            const atTop = scrollTop <= 0;
            const atBottom = scrollTop + clientHeight >= scrollHeight;
            if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) return;
            el.scrollBy(0, e.deltaY);
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div className="flex flex-col">
            {clearable && (
              <button
                onClick={() => handleSelect('')}
                className={cn(
                  'flex items-center justify-between px-3 py-2 text-sm rounded-md',
                  'hover:bg-muted transition-colors text-muted-foreground',
                  isEmpty && 'bg-muted'
                )}
              >
                <span>{resolvedClearLabel}</span>
                {isEmpty && <Check className="h-4 w-4 text-primary" />}
              </button>
            )}
            {options.map((option) => {
              const OptionIcon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    'flex items-center justify-between px-3 py-2 text-sm rounded-md',
                    'hover:bg-muted transition-colors',
                    option.value === value && 'bg-muted'
                  )}
                >
                  <span className="flex items-center gap-2">
                    {OptionIcon && <OptionIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                    {option.color && (
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    {option.label}
                  </span>
                  {option.value === value && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
